import React, { useEffect, useState, useCallback, useRef } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { PublicLayout } from "../layout/PublicLayout";
import { PrivateLayout } from "../layout/PrivateLayout/PrivateLayout";

// Public screens
import Landing from "../screens/public/Landing/Index";
import Chat from "../screens/public/Chat/Index";
import NotFound from "../screens/public/NotFound/Index";
import BusinessProfile from "../screens/public/BusinessProfile/Index";
import FeatureDetailsPage from "../screens/public/FeatureDetails/FeatureDetailsPage";
import Login from "../screens/public/Login/Index";
import BusinessSearch from "../screens/public/BusinessSearch";
import ArticleDetail from "../screens/public/ArticleDetail";

// Private screens
import Dashboard from "../screens/private/Dashboard/Index";
import FirstTimeSetup from "../screens/private/FirstTimeSetup/Index";
import PrivateChat from "../screens/private/Chat/Index";
import Article from "../screens/private/Article/Index";
import BusinessTailoring from "../screens/private/BusinessTailoring/Index";
import ChatJourney from "../screens/private/ChatJourney/Index";
import ShopConfig from "../screens/private/ShopConfig/Index";
import { useAppStore } from "../store";
import { selectAuthSlice } from "../store/common/auth/slice";
import { selectUserRecordById } from "../store/common/userRecord/slice";
import { useLoader } from "../contexts/LoaderContext";

export const AppRoutes = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const startedRef = useRef(false);
  const { showLoader, hideLoader } = useLoader();
  const [initialLoad, setInitialLoad] = useState(true);

  const authState = useAppStore(selectAuthSlice);
  const isAuthenticated = !!authState?.isAuthenticated;
  const loading = !!authState?.isLoading;
  const authSession = authState?.session;
  const userRecord = useAppStore(selectUserRecordById(authSession?.uid));

  // Actions from store
  const initAuthListener = useAppStore(
    (state) => state.common?.auth?.initAuthListener
  );
  const getUserRecord = useAppStore(
    (state) => state.common?.userRecord?.getById
  );

  // Initialize auth listener once
  useEffect(() => {
    if (!startedRef.current && initAuthListener) {
      startedRef.current = true;
      initAuthListener();
    }
  }, [initAuthListener]);

  // Fetch user record when UID becomes available
  useEffect(() => {
    if (authSession?.uid && getUserRecord) {
      getUserRecord({
        id: authSession.uid,
        subscribe: true,
        prefetch: true,
      }).finally(() => setInitialLoad(false));
    }
  }, [authSession?.uid, getUserRecord]);

  // Navigation and guarding logic without custom hook
  useEffect(() => {
    if (loading) return;
    hideLoader();

    const path = location.pathname;
    const isEntry =
      path === "/" || path === "/login" || path === "/first-time-setup";

    // Define private-only prefixes (simple check)
    const privatePrefixes = [
      "/dashboard",
      "/first-time-setup",
      "/articles",
      "/business-tailoring",
      "/chat-journey",
      "/shop-config",
    ];
    const isPrivatePath = privatePrefixes.some((prefix) =>
      path.startsWith(prefix)
    );

    if (!isAuthenticated) {
      // Not authenticated: block private pages
      if (isPrivatePath) {
        navigate("/login", { replace: true });
      }
      return;
    }

    // Authenticated: redirect entry pages to appropriate target
    if (initialLoad && !userRecord?.setupCompleted) {
      showLoader("Fetching user data...");
      return;
    }
    hideLoader();
    if (isEntry) {
      const setupDone =
        userRecord?.setupCompleted ?? authSession?.setupCompleted;
      const target = setupDone ? "/dashboard" : "/first-time-setup";
      if (path !== target) {
        navigate(target, { replace: true });
      }
    }
  }, [
    loading,
    isAuthenticated,
    authSession?.setupCompleted,
    userRecord?.setupCompleted,
    location.pathname,
    navigate,
  ]);

  // Helper component to conditionally render layout based on auth state
  const ConditionalLayout = useCallback(
    ({ children, ...layoutProps }) => {
      if (loading) return <div>Loading...</div>;

      return isAuthenticated ? (
        <PrivateLayout {...layoutProps}>{children}</PrivateLayout>
      ) : (
        <PublicLayout {...layoutProps}>{children}</PublicLayout>
      );
    },
    [isAuthenticated, loading]
  );

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/"
        element={
          <ConditionalLayout>
            <Landing />
          </ConditionalLayout>
        }
      />
      <Route
        path="/login"
        element={
          <ConditionalLayout>
            <Login />
          </ConditionalLayout>
        }
      />

      {/* Routes accessible with both public and private layouts */}
      <Route
        path="/map"
        element={
          <ConditionalLayout>
            <BusinessSearch />
          </ConditionalLayout>
        }
      />
      <Route
        path=":businessName/chat"
        element={
          <ConditionalLayout>
            <Chat />
          </ConditionalLayout>
        }
      />
      <Route
        path=":businessName"
        element={
          <ConditionalLayout>
            <BusinessProfile />
          </ConditionalLayout>
        }
      />
      <Route
        path=":businessName/:shopId/:featureName"
        element={
          <ConditionalLayout>
            <FeatureDetailsPage />
          </ConditionalLayout>
        }
      />
      <Route
        path="/article/:id"
        element={
          <ConditionalLayout>
            <ArticleDetail />
          </ConditionalLayout>
        }
      />

      {/* Private routes */}
      <Route
        path="/dashboard"
        element={
          <ConditionalLayout>
            <Dashboard />
          </ConditionalLayout>
        }
      />
      <Route
        path="/first-time-setup"
        element={
          <ConditionalLayout hideSidePanel>
            <FirstTimeSetup />
          </ConditionalLayout>
        }
      />
      <Route
        path="/articles"
        element={
          <ConditionalLayout>
            <Article />
          </ConditionalLayout>
        }
      />
      <Route
        path="/business-tailoring"
        element={
          <ConditionalLayout>
            <BusinessTailoring />
          </ConditionalLayout>
        }
      />
      <Route
        path="/chat-journey"
        element={
          <ConditionalLayout>
            <ChatJourney />
          </ConditionalLayout>
        }
      />
      <Route
        path="/shop-config"
        element={
          <ConditionalLayout>
            <ShopConfig />
          </ConditionalLayout>
        }
      />

      {/* Fallback */}
      <Route
        path="*"
        element={
          <ConditionalLayout>
            <NotFound />
          </ConditionalLayout>
        }
      />
    </Routes>
  );
};
